import { memo } from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import { Zap, BarChart3, MessageSquare, Link as LinkIcon } from "lucide-react";
import brainVideo from "../assets/images/generate_gif_for_my_website.mp4";

const FloatingAI = () => {
  const x = useMotionValue(250);
  const y = useMotionValue(230);
  const rotateX = useTransform(y, [0, 460], [15, -15]);
  const rotateY = useTransform(x, [0, 500], [-15, 15]);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    x.set(e.clientX - rect.left);
    y.set(e.clientY - rect.top);
  };

  const handleMouseLeave = () => {
    x.set(250);
    y.set(230);
  };

  return (
    <div
      className="relative mx-auto h-[500px] sm:h-[600px] w-full max-w-[650px] flex items-center justify-center cursor-pointer group overflow-visible"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ perspective: 1200 }}
    >
      {/* Central glow aura behind core */}
      <div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full blur-[80px]"
        style={{
          width: 320,
          height: 320,
          background: "radial-gradient(circle, rgba(0, 210, 255, 0.25), transparent 70%)",
        }}
      />

      {/* SVG Connecting Lines to Module Cards */}
      <svg
        className="absolute inset-0 h-full w-full pointer-events-none"
        viewBox="0 0 500 460"
        fill="none"
      >
        <defs>
          <filter id="glow-purple-strong" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="8" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
          <filter id="glow-blue-strong" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="8" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        {/* Top Left (Automation) */}
        <path
          d="M 250 240 C 180 220 120 180 90 140"
          stroke="#7C4DFF"
          strokeWidth="2"
          strokeOpacity="0.6"
          filter="url(#glow-purple-strong)"
          strokeDasharray="6 8"
          style={{ animation: "circuit-flow 6s linear infinite" }}
        />
        {/* Bottom Left (Analytics) */}
        <path
          d="M 250 280 C 180 290 120 310 90 320"
          stroke="#00D2FF"
          strokeWidth="2"
          strokeOpacity="0.6"
          filter="url(#glow-blue-strong)"
          strokeDasharray="6 8"
          style={{ animation: "circuit-flow 6s linear infinite reverse" }}
        />
        {/* Top Right (Chatbots) */}
        <path
          d="M 250 240 C 320 220 380 180 410 140"
          stroke="#00D2FF"
          strokeWidth="2"
          strokeOpacity="0.6"
          filter="url(#glow-blue-strong)"
          strokeDasharray="6 8"
          style={{ animation: "circuit-flow 6s linear infinite" }}
        />
        {/* Bottom Right (Integration) */}
        <path
          d="M 250 280 C 320 290 380 310 410 320"
          stroke="#7C4DFF"
          strokeWidth="2"
          strokeOpacity="0.6"
          filter="url(#glow-purple-strong)"
          strokeDasharray="6 8"
          style={{ animation: "circuit-flow 6s linear infinite reverse" }}
        />
      </svg>

      {/* ── 3D Holographic Core ───────────────────────── */}
      <motion.div
        className="absolute z-20 flex items-center justify-center pointer-events-none"
        style={{
          width: "550px",
          height: "550px",
          rotateX: rotateX,
          rotateY: rotateY,
          transformStyle: "preserve-3d",
        }}
        animate={{ y: [0, -12, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      >
        <div className="relative w-full h-full flex items-center justify-center" style={{ transformStyle: "preserve-3d" }}>

          {/* Rotating SVG HUD Grid */}
          <div className="absolute inset-0 flex items-center justify-center scale-[1.1] opacity-35 pointer-events-none" style={{ transform: "translateZ(-40px)" }}>
            <motion.svg
              width="380"
              height="380"
              viewBox="0 0 380 380"
              fill="none"
              className="w-[380px] h-[380px]"
              animate={{ rotate: 360 }}
              transition={{ duration: 45, repeat: Infinity, ease: "linear" }}
            >
              <circle cx="190" cy="190" r="170" stroke="#00D2FF" strokeWidth="1.5" strokeOpacity="0.4" strokeDasharray="3 6" />
              <circle cx="190" cy="190" r="160" stroke="#7C4DFF" strokeWidth="1.5" strokeOpacity="0.3" strokeDasharray="12 180" />
              <circle cx="190" cy="190" r="150" stroke="#00D2FF" strokeWidth="1" strokeOpacity="0.2" />
              <circle cx="190" cy="190" r="130" stroke="#7C4DFF" strokeWidth="1.5" strokeOpacity="0.3" strokeDasharray="40 10" />
              <circle cx="190" cy="190" r="110" stroke="#00D2FF" strokeWidth="1" strokeOpacity="0.15" strokeDasharray="6 4" />
              <circle cx="190" cy="190" r="90" stroke="#7C4DFF" strokeWidth="2" strokeOpacity="0.4" strokeDasharray="90 90" />
              <circle cx="190" cy="190" r="70" stroke="#00D2FF" strokeWidth="1" strokeOpacity="0.3" />
              <line x1="190" y1="10" x2="190" y2="370" stroke="#00D2FF" strokeWidth="1" strokeOpacity="0.15" strokeDasharray="5 5" />
              <line x1="10" y1="190" x2="370" y2="190" stroke="#00D2FF" strokeWidth="1" strokeOpacity="0.15" strokeDasharray="5 5" />
              <path d="M 190 25 L 180 25 M 190 25 L 200 25" stroke="#00D2FF" strokeWidth="2" strokeOpacity="0.6" />
              <path d="M 190 355 L 180 355 M 190 355 L 200 355" stroke="#00D2FF" strokeWidth="2" strokeOpacity="0.6" />
              <path d="M 25 190 L 25 180 M 25 190 L 25 200" stroke="#00D2FF" strokeWidth="2" strokeOpacity="0.6" />
              <path d="M 355 190 L 355 180 M 355 190 L 355 200" stroke="#00D2FF" strokeWidth="2" strokeOpacity="0.6" />
            </motion.svg>
          </div>

          {/* Pulsating radar rings */}
          <div className="absolute inset-0 flex items-center justify-center scale-95 opacity-50 pointer-events-none" style={{ transform: "translateZ(-20px)" }}>
            <motion.div
              className="absolute rounded-full border-2 border-dashed border-[#00D2FF]/30 w-[300px] h-[300px]"
              animate={{ rotate: 360 }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            />
            <motion.div
              className="absolute rounded-full border border-[#7C4DFF]/25 w-[250px] h-[250px] flex items-center justify-center"
              animate={{ rotate: -360 }}
              transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
            >
              <div className="absolute w-[230px] h-[1px] bg-gradient-to-r from-transparent via-[#7C4DFF]/50 to-transparent" />
              <div className="absolute h-[230px] w-[1px] bg-gradient-to-b from-transparent via-[#7C4DFF]/50 to-transparent" />
            </motion.div>
            <motion.div
              className="absolute rounded-full bg-[#00D2FF]/5 w-[120px] h-[120px] border border-[#00D2FF]/20"
              animate={{ scale: [1, 2.5], opacity: [0.7, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeOut" }}
            />
            <motion.div
              className="absolute rounded-full bg-[#7C4DFF]/5 w-[120px] h-[120px] border border-[#7C4DFF]/15"
              animate={{ scale: [1, 1.8], opacity: [0.5, 0] }}
              transition={{ duration: 4, delay: 2, repeat: Infinity, ease: "easeOut" }}
            />
          </div>

          {/* Hologram orbit rings */}
          <motion.div
            className="absolute rounded-full border border-[#00D2FF]/20"
            style={{ width: 310, height: 310, transformStyle: "preserve-3d", rotateX: "70deg", transform: "translateZ(10px)" }}
            animate={{ rotateZ: 360 }}
            transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
          />
          <motion.div
            className="absolute rounded-full border border-[#7C4DFF]/15"
            style={{ width: 340, height: 340, transformStyle: "preserve-3d", rotateX: "75deg", rotateY: "15deg", transform: "translateZ(20px)" }}
            animate={{ rotateZ: -360 }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          />

          {/* ── Neural Brain 3D Video ──── */}
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-[750px] aspect-video object-cover mix-blend-screen pointer-events-none"
            style={{
              transform: "translateZ(30px)",
              filter: "drop-shadow(0 0 55px rgba(0,100,255,0.8)) contrast(1.5) brightness(1.35) saturate(1.2)",
              maskImage: "radial-gradient(closest-side, black 30%, transparent 100%)",
              WebkitMaskImage: "radial-gradient(closest-side, black 50%, transparent 100%)",
            }}
          >
            <source src={brainVideo.replace(/\.mp4$/, ".webm")} type="video/webm" />
            <source src={brainVideo} type="video/mp4" />
          </video>
        </div>
      </motion.div>

      {/* ── 4 Floating Module Cards ──────────────────── */}
      {/* 1. Automation (Top Left) */}
      <motion.div
        className="absolute z-30"
        style={{ left: "10px", top: "100px" }}
        animate={{ y: [0, -8, 0] }}
        transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
      >
        <div
          className="flex flex-col items-center justify-center w-[90px] h-[90px] rounded-2xl border transition-colors duration-300"
          style={{
            background: "rgba(10, 16, 41, 0.85)",
            borderColor: "rgba(124, 77, 255, 0.25)",
            boxShadow: "0 0 20px rgba(124, 77, 255, 0.1), 0 8px 32px rgba(0, 0, 0, 0.4)",
          }}
        >
          <div className="w-10 h-10 rounded-xl bg-[#7C4DFF]/10 flex items-center justify-center mb-1 border border-[#7C4DFF]/20">
            <Zap className="w-5 h-5 text-[#7C4DFF]" />
          </div>
          <span className="text-[10px] font-bold text-slate-300 tracking-wider">AI Automation</span>
        </div>
      </motion.div>

      {/* 2. Chatbots (Top Right) */}
      <motion.div
        className="absolute z-30"
        style={{ right: "10px", top: "100px" }}
        animate={{ y: [0, -8, 0] }}
        transition={{ duration: 4.2, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
      >
        <div
          className="flex flex-col items-center justify-center w-[90px] h-[90px] rounded-2xl border transition-colors duration-300"
          style={{
            background: "rgba(10, 16, 41, 0.85)",
            borderColor: "rgba(39, 184, 255, 0.25)",
            boxShadow: "0 0 20px rgba(39, 184, 255, 0.1), 0 8px 32px rgba(0, 0, 0, 0.4)",
          }}
        >
          <div className="w-10 h-10 rounded-xl bg-[#27B8FF]/10 flex items-center justify-center mb-1 border border-[#27B8FF]/20">
            <MessageSquare className="w-5 h-5 text-[#27B8FF]" />
          </div>
          <span className="text-[10px] font-bold text-slate-300 tracking-wider">AI Chatbots</span>
        </div>
      </motion.div>

      {/* 3. Analytics (Bottom Left) */}
      <motion.div
        className="absolute z-30"
        style={{ left: "10px", bottom: "100px" }}
        animate={{ y: [0, -8, 0] }}
        transition={{ duration: 4.8, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
      >
        <div
          className="flex flex-col items-center justify-center w-[90px] h-[90px] rounded-2xl border transition-colors duration-300"
          style={{
            background: "rgba(10, 16, 41, 0.85)",
            borderColor: "rgba(39, 184, 255, 0.25)",
            boxShadow: "0 0 20px rgba(39, 184, 255, 0.1), 0 8px 32px rgba(0, 0, 0, 0.4)",
          }}
        >
          <div className="w-10 h-10 rounded-xl bg-[#27B8FF]/10 flex items-center justify-center mb-1 border border-[#27B8FF]/20">
            <BarChart3 className="w-5 h-5 text-[#27B8FF]" />
          </div>
          <span className="text-[10px] font-bold text-slate-300 tracking-wider">AI Agents</span>
        </div>
      </motion.div>

      {/* 4. Integration (Bottom Right) */}
      <motion.div
        className="absolute z-30"
        style={{ right: "10px", bottom: "100px" }}
        animate={{ y: [0, -8, 0] }}
        transition={{ duration: 4.6, repeat: Infinity, ease: "easeInOut", delay: 0.8 }}
      >
        <div
          className="flex flex-col items-center justify-center min-w-[90px] min-h-[90px] rounded-2xl border transition-colors duration-300"
          style={{
            background: "rgba(10, 16, 41, 0.85)",
            borderColor: "rgba(124, 77, 255, 0.25)",
            boxShadow: "0 0 20px rgba(124, 77, 255, 0.15), 0 8px 32px rgba(0, 0, 0, 0.4)",
          }}
        >
          <div className="w-10 h-10 rounded-xl bg-[#7C4DFF]/10 flex items-center justify-center mb-1 border border-[#7C4DFF]/20">
            <LinkIcon className="w-5 h-5 text-[#7C4DFF]" />
          </div>
          <span className="text-[10px] px-2.5 font-bold text-slate-300 tracking-wider">Apps Development</span>
        </div>
      </motion.div>
    </div>
  );
};

export default memo(FloatingAI);
